/**
 * Module dependencies.
 */

import { Button } from 'app/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from 'app/components/ui/dialog';
import { useTranslation } from 'react-i18next';
import Cropper, { Area } from 'react-easy-crop';
import React, { useCallback, useState } from 'react';

/**
 * Output size (square) of the cropped avatar, in pixels.
 */

const OUTPUT_SIZE = 256;

/**
 * `Props` type.
 */

type Props = {
  imageSrc: string;
  open: boolean;
  saving?: boolean;
  onCancel: () => void;
  onCropped: (file: File) => void;
};

/**
 * Load an image element from a source URL.
 */

function createImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();

    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', error => reject(error));
    image.src = url;
  });
}

/**
 * Crop + resize the selected region to a square JPEG on a canvas.
 */

async function getCroppedFile(imageSrc: string, area: Area): Promise<File> {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');

  canvas.width = OUTPUT_SIZE;
  canvas.height = OUTPUT_SIZE;

  const context = canvas.getContext('2d');

  if (context) {
    context.drawImage(image, area.x, area.y, area.width, area.height, 0, 0, OUTPUT_SIZE, OUTPUT_SIZE);
  }

  const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.9));

  return new File([blob as Blob], 'avatar.jpg', { type: 'image/jpeg' });
}

/**
 * Export `AvatarCropDialog` component.
 */

export function AvatarCropDialog({ imageSrc, open, saving, onCancel, onCropped }: Props): JSX.Element {
  const [translate] = useTranslation();
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [area, setArea] = useState<Area | null>(null);

  const onCropComplete = useCallback((_: Area, areaPixels: Area) => {
    setArea(areaPixels);
  }, []);

  const onSave = useCallback(async () => {
    if (!area) {
      return;
    }

    onCropped(await getCroppedFile(imageSrc, area));
  }, [area, imageSrc, onCropped]);

  return (
    <Dialog onOpenChange={value => !value && onCancel()} open={open}>
      <DialogContent className={'sm:max-w-[420px]'}>
        <DialogHeader>
          <DialogTitle>{translate('settings.profile.photo.cropTitle')}</DialogTitle>
        </DialogHeader>

        <div className={'relative h-64 w-full overflow-hidden rounded-lg bg-muted'}>
          <Cropper
            aspect={1}
            crop={crop}
            cropShape={'round'}
            image={imageSrc}
            onCropChange={setCrop}
            onCropComplete={onCropComplete}
            onZoomChange={setZoom}
            showGrid={false}
            zoom={zoom}
          />
        </div>

        <div className={'flex items-center gap-3'}>
          <span className={'text-xs text-muted-foreground'}>{translate('settings.profile.photo.zoom')}</span>

          <input
            className={'h-1 flex-1 cursor-pointer appearance-none rounded-full bg-border accent-primary'}
            max={3}
            min={1}
            onChange={event => setZoom(Number(event.target.value))}
            step={0.05}
            type={'range'}
            value={zoom}
          />
        </div>

        <DialogFooter>
          <Button disabled={saving} onClick={onCancel} type={'button'} variant={'outline'}>
            {translate('common.actions.cancel')}
          </Button>

          <Button disabled={saving || !area} onClick={onSave} type={'button'}>
            {saving ? translate('settings.profile.photo.saving') : translate('settings.profile.photo.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
